import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Instagram, 
  MousePointer2, 
  MessageSquare, 
  Mail, 
  Link as LinkIcon, 
  ShoppingCart, 
  CheckCircle2, 
  TrendingUp, 
  BarChart3, 
  Target,
  ChevronRight,
  ArrowRight
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip,
  CartesianGrid 
} from 'recharts';
import './Attribution.css';

const Attribution = () => {
  const [platform, setPlatform] = useState('Instagram');

  const attributionSteps = [
    { icon: MousePointer2, label: 'Post', val: '8.4M', drop: null },
    { icon: MessageSquare, label: 'Comment', val: '1.2M', drop: '85%' },
    { icon: Mail, label: 'DM Sent', val: '420K', drop: '65%' },
    { icon: LinkIcon, label: 'Click', val: '92K', drop: '78%' },
    { icon: ShoppingCart, label: 'Checkout', val: '15.4K', drop: '83%' },
    { icon: CheckCircle2, label: 'Purchase', val: '5.2K', drop: '66%' },
  ];

  // Mock data for charts
  const revenuePerPost = [
    { name: 'P1', value: 1200 },
    { name: 'P2', value: 2400 },
    { name: 'P3', value: 1800 },
    { name: 'P4', value: 3100 },
    { name: 'P5', value: 2800 },
  ];

  const dmReplies = [
    { name: 'Mon', value: 450 },
    { name: 'Tue', value: 680 },
    { name: 'Wed', value: 520 },
    { name: 'Thu', value: 890 },
    { name: 'Fri', value: 1200 },
  ];

  const conversionRate = [
    { name: 'W1', value: 2.4 },
    { name: 'W2', value: 3.1 },
    { name: 'W3', value: 2.8 },
    { name: 'W4', value: 4.2 },
  ];

  const timeToPurchase = [
    { name: 'Jan', value: 48 },
    { name: 'Feb', value: 42 },
    { name: 'Mar', value: 36 },
    { name: 'Apr', value: 24 },
  ];

  const chartConfigs = [
    { title: 'Revenue per Post', subtitle: 'Performance by media unit', data: revenuePerPost, color: '#3B82F6' },
    { title: 'DM Replies Triggered', subtitle: 'Engagement velocity', data: dmReplies, color: '#A855F7' },
    { title: 'Conversion Rate', subtitle: 'Efficiency over time', data: conversionRate, color: '#10B981' },
    { title: 'Avg Time to Purchase', subtitle: 'Hours from first contact', data: timeToPurchase, color: '#F97316' },
  ];

  return (
    <>
      <Helmet>
        <title>Attribution Map - Revexia AI Revenue Brain</title>
        <meta name="description" content="Revenue attribution flow and conversion funnel" />
      </Helmet>

      <div className="attribution-page-wrapper">
        <div className="container-fluid px-4">
          {/* Top Controls */}
          <div className="attribution-header">
            <div className="attribution-header-left">
              <h2 className="attribution-title">Revenue Attribution Flow</h2>
              <p className="attribution-subtitle">Tracing every dollar back to its content origin.</p>
            </div>
            <div className="platform-tabs">
              {['Instagram', 'Facebook', 'TikTok'].map(tab => (
                <button 
                  key={tab}
                  onClick={() => setPlatform(tab)}
                  className={`platform-tab-btn ${platform === tab ? 'active' : ''}`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Funnel Section */}
          <section className="funnel-section">
            <div className="funnel-card">
              <div className="funnel-bg-glow top-right"></div>
              <div className="funnel-bg-glow bottom-left"></div>
              
              <div className="funnel-content">
                <div className="funnel-header-section">
                  <span className="funnel-badge">Cross-Channel Attribution</span>
                  <h3 className="funnel-title">Diagnostic Conversion Funnel</h3>
                </div>
                
                {/* Flow Map */}
                <div className="flow-map-container">
                  <div className="flow-map-wrapper">
                    {attributionSteps.map((step, index) => (
                      <React.Fragment key={index}>
                        <div className="flow-step">
                          <div className="flow-step-icon">
                            <step.icon size={20} />
                          </div>
                          <div className="flow-step-info">
                            <p className="flow-step-label">{step.label}</p>
                            <p className="flow-step-value">{step.val}</p>
                          </div>
                          {step.drop && (
                            <div className="flow-step-drop">-{step.drop}</div>
                          )}
                        </div>
                        {index < attributionSteps.length - 1 && (
                          <div className="flow-connector">
                            <ChevronRight size={20} className="flow-connector-icon" />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="funnel-summary">
                  <div className="funnel-stat">
                    <p className="funnel-stat-label">Total Reach</p>
                    <p className="funnel-stat-value">8.42M</p>
                  </div>
                  <div className="funnel-divider"></div>
                  <div className="funnel-stat">
                    <p className="funnel-stat-label">Overall CR</p>
                    <p className="funnel-stat-value emerald">0.06%</p>
                  </div>
                  <div className="funnel-divider"></div>
                  <div className="funnel-stat">
                    <p className="funnel-stat-label">Attributed Rev</p>
                    <p className="funnel-stat-value blue">$842,102</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Metrics Grid */}
          <section className="metrics-section">
            <div className="metrics-section-header">
              <div className="metrics-icon-wrapper">
                <BarChart3 size={20} />
              </div>
              <h3 className="metrics-section-title">Content Revenue Metrics</h3>
            </div>

            <div className="row g-4">
              {chartConfigs.map((config, index) => (
                <div key={index} className="col-12 col-sm-6 col-xl-3">
                  <div className="metric-chart-card">
                    <div className="metric-chart-header">
                      <div className="metric-chart-info">
                        <h4 className="metric-chart-title">{config.title}</h4>
                        <p className="metric-chart-subtitle">{config.subtitle}</p>
                      </div>
                    </div>
                    <div className="metric-chart-body">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={config.data}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                          <XAxis 
                            dataKey="name" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                          />
                          <YAxis 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'rgba(13, 15, 41, 0.95)', 
                              border: '1px solid rgba(255,255,255,0.1)', 
                              borderRadius: '12px',
                              backdropFilter: 'blur(4px)'
                            }}
                            itemStyle={{ color: '#fff' }}
                          />
                          <Bar 
                            dataKey="value" 
                            fill={config.color}
                            radius={[8, 8, 0, 0]}
                            animationDuration={1000}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Bottom Insight */}
          <div className="efficiency-insight-card">
            <div className="efficiency-insight-left">
              <div className="efficiency-icon-wrapper">
                <Target size={28} />
              </div>
              <div className="efficiency-info">
                <h4 className="efficiency-title">Platform Efficiency Score</h4>
                <p className="efficiency-description">
                  Instagram is outperforming TikTok in conversion velocity by 22%.
                </p>
              </div>
            </div>
            <button className="efficiency-action-btn">
              <span>Optimized Strategy</span>
              <TrendingUp size={16} className="efficiency-icon emerald" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Attribution;
